import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import styles from './AiSearchAssistant.module.css';
import { Mic, X, Send, Bot, Loader2, Sparkles } from 'lucide-react';
import PostcodeAutocomplete from '../PostcodeAutocomplete/PostcodeAutocomplete';

const AiSearchAssistant = () => {
    const navigate = useNavigate();
    const ENABLE_VOICE = false; // Set to TRUE to re-enable voice

    // Refs
    const inputRef = useRef(null);
    const messagesEndRef = useRef(null);

    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [voiceStatus, setVoiceStatus] = useState("idle");

    // Search Flow State
    // Steps: init -> profession -> profession_select -> profession_search (loop) -> location -> redirecting
    const [step, setStep] = useState("init");
    const [searchParams, setSearchParams] = useState({
        professionId: null,
        professionName: "",
        postcode: "",
        suburb: "",
        state: "",
    });

    const [suggestedProfessions, setSuggestedProfessions] = useState([]);

    // Mutable ref for async access (avoids stale closure on searchParams)
    const searchParamsRef = useRef({ professionId: null, professionName: "", postcode: "", suburb: "", state: "" });

    // Auto-scroll
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // Auto-focus input when step changes
    useEffect(() => {
        if (isOpen && step !== 'redirecting' && step !== 'location' && voiceStatus === 'idle') {
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [isOpen, step, voiceStatus]);

    // --- TTS & STT ---
    const speak = async (text) => {
        if (!ENABLE_VOICE) return;

        try {
            setVoiceStatus("speaking");
            const response = await axios.post(
                `${API_BASE_URL}/api/AiSearch/speak`,
                { text: text },
                { responseType: 'blob' }
            );
            const audioUrl = URL.createObjectURL(response.data);
            const audio = new Audio(audioUrl);
            audio.play();
            audio.onended = () => setVoiceStatus("idle");
            audio.onerror = () => setVoiceStatus("idle");
        } catch (error) {
            console.error("TTS Error", error);
            setVoiceStatus("idle");
        }
    };

    const listen = () => {
        if (!ENABLE_VOICE) return;

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            addBotMessage("Voice input isn't supported in this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-AU';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        setVoiceStatus("listening");
        recognition.start();

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInputText(transcript);
            handleUserResponse(transcript);
        };

        recognition.onerror = () => setVoiceStatus("idle");
        recognition.onend = () => {
            if (voiceStatus === 'listening') setVoiceStatus("idle");
        };
    };

    // --- MESSAGING HELPERS ---
    const addBotMessage = (text, options = []) => {
        setMessages(prev => [...prev, { sender: 'bot', text, options }]);
        speak(text);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { sender: 'user', text }]);
    };

    // --- HELPER: SEARCH PROFESSION (matches JobCreationAssistant pattern) ---
    const searchAndSuggestProfession = async (query) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/Jobs/AISmartSearch?search=${encodeURIComponent(query)}`);
            const matches = res.data || [];

            if (matches.length > 0) {
                setSuggestedProfessions(matches);
                const options = matches.slice(0, 5).map(p => p.name);
                options.push("None of these");

                setStep("profession_select");
                addBotMessage(`I found these professionals for "${query}". Please select the best match:`, options);
            } else {
                setStep("profession_search");
                addBotMessage(`I couldn't find a profession matching "${query}". Could you try a different trade name? (e.g. Carpenter, Plumber)`);
            }
        } catch (e) {
            console.error(e);
            setStep("profession_search");
            addBotMessage("I had trouble searching. What is the profession name?");
        }
    };

    // --- LOGIC FLOW ---
    const startConversation = () => {
        setIsOpen(true);
        if (messages.length === 0) {
            setStep("profession");
            addBotMessage("Hi! I'm your Search Assistant.\n\nWhat kind of professional are you looking for? You can type a trade name (e.g. Plumber) or describe what you need done (e.g. \"fix my leaking tap\").");
        }
    };

    const handleUserResponse = async (text) => {
        setVoiceStatus("processing");
        addUserMessage(text);

        // --- STEP 1: INITIAL PROFESSION SEARCH ---
        if (step === "profession") {
            await searchAndSuggestProfession(text);
        }

        // --- STEP 1b: PROFESSION SELECTION FROM CHIPS ---
        else if (step === "profession_select") {
            // Check if user clicked "None of these"
            if (text.toLowerCase().includes("none") || text.toLowerCase().includes("no")) {
                setStep("profession_search");
                addBotMessage("No problem. What is the name of the trade or profession you are looking for? (e.g. Electrician, Builder)");
                setVoiceStatus("idle");
                return;
            }

            // Check if text matches one of the suggestions
            const match = suggestedProfessions.find(p => p.name.toLowerCase() === text.toLowerCase());

            if (match) {
                selectProfession(match);
            } else {
                // User typed something new, treat as a new search
                await searchAndSuggestProfession(text);
            }
        }

        // --- STEP 1c: MANUAL PROFESSION SEARCH ---
        else if (step === "profession_search") {
            await searchAndSuggestProfession(text);
        }

        // --- STEP 2: LOCATION (handled by PostcodeAutocomplete, but fallback for typed text) ---
        else if (step === "location") {
            // If user types and hits enter instead of using autocomplete,
            // try to resolve it via the suggest API
            try {
                const locRes = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${encodeURIComponent(text)}`);

                if (locRes.data && locRes.data.length > 0) {
                    if (locRes.data.length === 1) {
                        // Single match — use it directly
                        const loc = locRes.data[0];
                        completeLocationAndRedirect(loc);
                    } else {
                        // Multiple matches — present as options
                        const options = locRes.data.slice(0, 5).map(l => l.displayName);
                        setMessages(prev => [...prev, {
                            sender: 'bot',
                            text: `I found a few locations matching "${text}". Which one?`,
                            options,
                            locationData: locRes.data.slice(0, 5)
                        }]);
                        speak(`I found a few locations matching "${text}". Which one?`);
                        setStep("location_select");
                    }
                } else {
                    addBotMessage("I couldn't find that location. Please try typing a suburb name or postcode.");
                }
            } catch (e) {
                console.error(e);
                addBotMessage("I'm having trouble looking up that location. Please try again.");
            }
        }

        // --- STEP 2b: LOCATION SELECTION FROM CHIPS ---
        else if (step === "location_select") {
            // Find the matching location from the last bot message with locationData
            const lastLocationMsg = [...messages].reverse().find(m => m.locationData);
            if (lastLocationMsg) {
                const match = lastLocationMsg.locationData.find(l => l.displayName === text);
                if (match) {
                    completeLocationAndRedirect(match);
                } else {
                    // User typed something new, search again
                    setStep("location");
                    await handleUserResponse(text);
                    return;
                }
            }
        }

        setVoiceStatus("idle");
    };

    // Helper to set profession and move to location step
    const selectProfession = (prof) => {
        searchParamsRef.current.professionId = prof.id;
        searchParamsRef.current.professionName = prof.name;
        setSearchParams(prev => ({ ...prev, professionId: prof.id, professionName: prof.name }));
        setStep("location");
        addBotMessage(`Great choice! Looking for a ${prof.name}.\n\nWhere do you need them? Start typing a suburb or postcode below.`);
    };

    // Helper for PostcodeAutocomplete selection
    const handleLocationSelect = (locationData) => {
        // locationData = { fullAddress, postcode, suburb, state }
        addUserMessage(locationData.fullAddress || `${locationData.suburb}, ${locationData.state}`);

        const loc = {
            postcode: locationData.postcode,
            displayName: locationData.fullAddress || `${locationData.suburb}, ${locationData.state} ${locationData.postcode}`,
            suburb: locationData.suburb,
            state: locationData.state
        };

        completeLocationAndRedirect(loc);
    };

    // Helper to finalize location and redirect
    const completeLocationAndRedirect = (loc) => {
        searchParamsRef.current.postcode = loc.postcode;
        searchParamsRef.current.suburb = loc.suburb || loc.displayName;
        searchParamsRef.current.state = loc.state || "";
        setSearchParams(prev => ({
            ...prev,
            postcode: loc.postcode,
            suburb: loc.suburb || loc.displayName,
            state: loc.state || "",
        }));

        const profName = searchParamsRef.current.professionName;
        setStep("redirecting");
        addBotMessage(`Searching for ${profName} near ${loc.displayName}...`);

        setTimeout(() => {
            performSearchRedirect(profName, loc.postcode);
        }, 1500);
    };

    // Helper to Redirect
    const performSearchRedirect = (category, postcode) => {
        setIsOpen(false);
        navigate(`/map-search-v2?searchTerm=${encodeURIComponent(category)}&postCode=${encodeURIComponent(postcode)}&type=Profession&radius=25`);
    };

    // UI Inputs
    const handleSend = () => {
        if (!inputText.trim()) return;
        handleUserResponse(inputText);
        setInputText("");
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSend();
    };

    if (!isOpen) {
        return (
            <button className={styles.fab} onClick={startConversation} title="AI Search Assistant">
                <Bot className={styles.fabIcon} />
            </button>
        );
    }

    return (
        <div className={styles.chatWindow}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <Sparkles size={18} /> Build Start AI Search
                </div>
                <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
                    <X size={20} />
                </button>
            </div>

            {/* Chat List */}
            <div className={styles.messageList}>
                {messages.map((msg, i) => (
                    <div key={i} className={`${styles.messageRow} ${msg.sender === 'user' ? styles.userRow : styles.botRow}`}>
                        <div className={`${styles.bubble} ${msg.sender === 'user' ? styles.userBubble : styles.botBubble}`}>
                            <div style={{whiteSpace:'pre-wrap'}}>{msg.text}</div>
                            {msg.options && (
                                <div className={styles.optionsContainer}>
                                    {msg.options.map(opt => (
                                        <button key={opt} className={styles.optionChip} onClick={() => handleUserResponse(opt)}>
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {voiceStatus === 'processing' && (
                    <div className={styles.messageRow}>
                        <div className={`${styles.bubble} ${styles.botBubble}`}>
                            <Loader2 size={16} className={styles.spin} /> Thinking...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {step !== 'redirecting' && (
                <div className={styles.inputArea}>
                    {ENABLE_VOICE && (
                        <button
                            className={`${styles.micBtn} ${voiceStatus === 'listening' ? styles.micBtnActive : ''}`}
                            onClick={listen}
                            disabled={voiceStatus === 'processing' || voiceStatus === 'speaking'}
                        >
                            {voiceStatus === 'processing' ? <Loader2 className={styles.spin} size={20} /> : <Mic size={20} />}
                        </button>
                    )}

                    {step === 'location' ? (
                        <div style={{flex: 1, marginRight: '8px'}}>
                            <PostcodeAutocomplete
                                value={inputText}
                                onChange={(val) => setInputText(val)}
                                onSuggestionSelect={handleLocationSelect}
                                placeholder="Start typing suburb or postcode..."
                                autoFocus
                            />
                        </div>
                    ) : (
                        <input
                            ref={inputRef}
                            className={styles.input}
                            placeholder={step === 'profession' || step === 'profession_search'
                                ? "e.g. Plumber, fix my roof..."
                                : "Type here..."
                            }
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={voiceStatus !== 'idle'}
                        />
                    )}

                    {step !== 'location' && (
                        <button className={styles.micBtn} onClick={handleSend} disabled={!inputText.trim()}>
                            <Send size={18} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AiSearchAssistant;
